import './Application.scss';
import './Application.m.scss';
import { Container } from '@components/Component/Container/Container.js';
import { Request } from '@components/Request/Request.js';
import { of, ReplaySubject } from 'rxjs';
import { isMobile } from '@components/Utility/MobileDetect.js';
import DIContainer from '@components/DependencyInjectionContainer.js';
import { CONFIG, ENV, PERM, TOKEN } from '@components/Environment.js';
import { convertLegacyQ360Url, createComponentsFromDOM, setCookie, zoomHandler } from '@components/Utility/index.js';
import { DateTimeManager } from '@components/DateTimeManager/DateTimeManager.js';
import { switchMap } from 'rxjs/operators';
/**
 * Class `Application` extends the `Container` class.
 * It represents the overall application interface with the main components such as `Navbar`, and `Viewport`.
 */
export class Application extends Container {
    get SessionTimeoutManager() {
        if (!this.dependencies.SessionTimeoutManager) {
            // we didnt receive a request dependency injection, so just use the global singleton
            this.dependencies.SessionTimeoutManager = DIContainer.getInstance('SessionTimeoutManager');
        }
        return this.dependencies.SessionTimeoutManager;
    }
    /**
     * Request getter which handles lazy loading the `Request` dependency. Supports injection for unit tests etc.
     */
    get Request() {
        if (!this.dependencies.Request) {
            // we didnt receive a request dependency injection, so just use the global singleton
            this.dependencies.Request = DIContainer.getInstance('Request');
        }
        return this.dependencies.Request;
    }
    constructor(id, rootElement, dependencies = {}) {
        super(id, rootElement, dependencies);
        this.componentName = 'Application';
        this.authRequired = true;
        this.configLoaded$ = new ReplaySubject(1);
        // global application level hotkeys
        this.hotkeys = {
            // alt+g defined in Q360.ts subclass
            '*': (event, key) => {
                const screenManager = DIContainer.getInstance('ScreenManager');
                const screen = screenManager.activeScreenGet();
                if (screen && screen.currentForm) {
                    if (screen.currentForm.hotkeys[key] && screen.currentForm.hotkeys[key](event, key)) {
                        return true;
                    }
                }
            }
        };
        DIContainer.singletons['Application'] = this; // avoid loop if something tries to getInstance of the Application before the constructor is done (ie. toolbar initializing buttons)
        this.updateDatetimeFormat('');
        this.initializeCache()
            .subscribe({
            next: () => {
                this.loadApplicationConfig();
            },
            error: (e) => {
                console.error(e);
                this.loadApplicationConfig();
            }
        });
        const navbar = DIContainer.getInstance('Navbar', 'navbar');
        const viewport = DIContainer.getInstance('Viewport', 'viewport');
        this.componentsAdd([
            navbar,
            viewport
        ]);
    }
    initializeCache() {
        return this.checkClientVersion().pipe(switchMap(() => this.checkClientUserId()));
    }
    checkClientUserId() {
        if (!ENV('userId')) {
            return of(true);
        }
        return DIContainer.getInstance('CacheManager').getCache('client', 'userId', { defaultReturnValue: '' }).pipe(switchMap((cachedUserId) => {
            if (cachedUserId !== ENV('userId')) {
                console.log(`New login detected. Deleting cache for ${cachedUserId}, new userid: ${ENV('userId')}.`);
                return this.setNewClientVersion();
            }
            else {
                return of(true);
            }
        }));
    }
    checkClientVersion() {
        return DIContainer.getInstance('CacheManager').getCache('client', 'clientVersion', { defaultReturnValue: '' }).pipe(switchMap((cachedVersion) => {
            if (!cachedVersion || cachedVersion !== ENV('clientVersion')) {
                return this.setNewClientVersion();
            }
            else {
                return of(true);
            }
        }));
    }
    setNewClientVersion() {
        return DIContainer.getInstance('CacheManager').deleteCache('request').pipe(switchMap(() => {
            return DIContainer.getInstance('CacheManager').setCache('client', 'clientVersion', ENV('clientVersion'));
        }), switchMap(() => {
            return DIContainer.getInstance('CacheManager').setCache('client', 'userId', ENV('userId'));
        }));
    }
    configReady$() {
        return this.configLoaded$;
    }
    clearCache() {
        DIContainer.getInstance('CacheManager').deleteCache('request').subscribe(); // for now this only clears the "request" cache but will not affect the "toolbar" cache which controls the recents menu
    }
    /**
     * ideally we dont want this ajax call to delay loading the application, even though that would possibly make some things easier
     * @private
     */
    loadApplicationConfig() {
        // TODO: need a caching strategy for this. maybe if is_dev is false or something cache it. 
        this.Request.GET({
            endpoint: `application/${ENV('appName')}` // ie. application/q360, application/login, or application/eaccept
            ,
            requestData: {
                browserlanguage: navigator.language
            },
            showErrors: false,
            cache: true,
            cacheStrategy: 'network-first' // TODO: would like for this to be optimized  
        }).subscribe({
            next: this.parseApplicationConfigResponse.bind(this),
            error: (err) => {
                this.configLoaded$.error(err);
                createComponentsFromDOM(document);
                DIContainer.getInstance('ScreenManager').open('error', {
                    title: 'Error',
                    requestParams: err
                });
            }
        });
    }
    parseApplicationConfigResponse(response) {
        const tokens = response.payload.tokens;
        tokens?.forEach((token) => {
            TOKEN(token.tokenFormat, token.tokenValue);
        });
        const configdata = response.payload.configdata || [];
        configdata?.forEach((configitem) => {
            CONFIG(configitem.configItem, configitem.configValue1, configitem.configValue2);
        });
        const perms = response.payload.perms || [];
        perms?.forEach((perms) => {
            PERM(perms.permName, perms.value);
        });
        const captions = response.payload.captions;
        const captionManager = DIContainer.getInstance('CaptionManager');
        captionManager.addCaptions(captions);
        const currencies = response.payload.currency;
        if (currencies && DIContainer.isSingletonRegistered('CurrencyManager')) {
            const currencyManager = DIContainer.getInstance('CurrencyManager');
            currencyManager?.addCurrencies(currencies);
        }
        // unsetCookie('XDEBUG_SESSION'); this makes debugging basically impossible when you need to reload the browser to hit a certain network request, dont know why we have this here.
        const userpreferences = response.payload.userpreferences;
        userpreferences?.forEach((pref) => {
            let prefVal = pref.value;
            if (prefVal === 'true') {
                prefVal = true;
            }
            if (prefVal === 'false') {
                prefVal = false;
            }
            ENV(pref.preference, prefVal);
            if (pref.preference === 'userDatetimeFormat' && pref.value) {
                this.updateDatetimeFormat(pref.value);
            }
            if (pref.preference === 'xdebug' && pref.value) {
                setCookie('XDEBUG_SESSION', 'PHPSTORM');
            }
        });
        this.updateCssVars();
        const environment = response.payload.environment;
        environment?.forEach((pref) => {
            let prefVal = pref.value;
            if (prefVal === 'true') {
                prefVal = true;
            }
            if (prefVal === 'false') {
                prefVal = false;
            }
            if (pref.variable === 'oAuthConfig') {
                ENV(pref.variable, JSON.parse(pref.value));
            }
            else {
                ENV(pref.variable, prefVal);
            }
        });
        if (ENV('theme')) {
            document.body.setAttribute('data-theme', ENV('theme'));
        }
        if (ENV('sizing')) {
            document.body.setAttribute('data-sizing', ENV('sizing'));
        }
        if (ENV('gridSizing')) {
            document.body.setAttribute('data-grid-sizing', ENV('gridSizing'));
        }
        if (ENV('contrast')) {
            document.body.setAttribute('data-contrast', ENV('contrast'));
        }
        const timezones = response.payload.timezones;
        if (timezones) {
            const timezoneMap = ENV('timezoneMapping');
            timezones.forEach((tz) => {
                timezoneMap[tz.TZName] = tz.SqlTimezone;
            });
        }
        if (this.authRequired) {
            const timeoutWarningMinutes = !parseInt(CONFIG('SESSIONTIMEOUT', 2)) ? 5 : parseInt(CONFIG('SESSIONTIMEOUT', 2));
            this.SessionTimeoutManager.setWarningThreshold(timeoutWarningMinutes);
        }
        this.configLoaded$.next(true);
    }
    draw() {
        this.componentGet('navbar').captionKey = `${ENV('appName')}_navbar`;
        super.draw();
        this.rootElement.classList.add('s360Application');
        const tooltipManager = DIContainer.getInstance('TooltipManager');
        tooltipManager.draw();
        document.body.appendChild(tooltipManager.rootElement);
        const toastManager = DIContainer.getInstance('ToastManager');
        toastManager.draw();
        document.body.appendChild(toastManager.rootElement);
        // TODO: not sure if we are going to use this methodology at all or if we will just use a mobile css file. 
        if (isMobile()) {
            this.rootElement.classList.add('s360MobileView');
        }
        this.registerEvent('fullscreenchange', document, () => {
            if (this.fullscreenElement) {
                this.fullscreenElement.classList.remove('fullscreen');
            }
            this.fullscreenElement = document.fullscreenElement;
            DIContainer.getInstance('ComponentRegistry').forEach((component) => {
                component.handleFullscreenSwitch(document.fullscreenElement);
            });
        });
        this.registerEvent('beforeunload', window, (e) => {
            let block = false;
            DIContainer.getInstance('ScreenManager').forEachScreen((screen) => {
                if (!block && screen.currentForm) {
                    block = screen.currentForm.hasUnSavedChanges || !!screen.currentForm.closePromptCaptionKey; // if it has a closePromptCaptionKey then its intended to block close with a prompt
                }
            });
            if (block) {
                e.preventDefault();
                e.returnValue = '';
            }
        });
        this.initFocusListener();
    }
    updateCssVars() {
        document.body.style.setProperty('--grid-row-bg-ev', ENV('gridAlternateColors') ? '' : 'var(--grid-row-bg)');
        document.body.style.setProperty('--grid-vertical-lines', ENV('gridVerticalLines') ? '1px solid var(--grid-border)' : 'none');
    }
    /**
     * Sets up a global event to delegate focus events for all components in the application.
     * This is a performance/memory optimization to reduce the number of event listeners required by our forms.
     * @protected
     */
    initFocusListener() {
        // Handle focusin event
        this.registerEvent('focusin', document, (event) => {
            this.handleComponentEvent(event, 'focus');
        });
        this.registerEvent('paste', document, (event) => {
            this.handleComponentEvent(event, 'paste');
        });
        // Handle focusout event
        this.registerEvent('focusout', document, (event) => {
            this.handleComponentEvent(event, 'blur');
        });
        this.registerEvent('keydown', document, (event) => {
            if (this.authRequired) {
                this.SessionTimeoutManager.pulse();
            }
            this.handleComponentEvent(event, 'keydown');
        });
        this.registerEvent('click', document, (event) => {
            if (this.authRequired) {
                this.SessionTimeoutManager.pulse();
            }
            let el = event.target;
            if (!el) {
                return;
            }
            el = el.closest('a');
            if (el && !el.dataset.noLinkIntercept) {
                const initiator = DIContainer.getInstance('ComponentRegistry').getComponentByElement(el);
                const anchor = el;
                const href = anchor.getAttribute('href');
                if (initiator && initiator.rootElement.dataset.ctrlClickAnchorInEditmode && !initiator.shouldDisable() && !event.ctrlKey) {
                    return; // if its in edit mode, they must hold the ctrl key to click a hyperlink, so that it's not tedious to edit the link
                }
                if (href) {
                    if (initiator && initiator.componentName === 'Tooltip') {
                        DIContainer.getInstance('TooltipManager').handleClickAway(event);
                    }
                    const lowerHref = href.toLowerCase();
                    if (lowerHref.startsWith('#') ||
                        lowerHref.startsWith('mailto:') ||
                        lowerHref.startsWith('tel:') ||
                        lowerHref.startsWith('sms:')
                    // lowerHref.startsWith('javascript:')
                    ) {
                        return;
                    }
                    event.preventDefault();
                    zoomHandler(href, {
                        target: '_top',
                        initiator,
                        event
                    });
                }
                return;
            }
        });
    }
    /**
     * Delegates an event to the applicable component.
     * @param event
     * @param type
     */
    handleComponentEvent(event, type /*| 'click'*/) {
        const targetElement = event.target;
        const component = DIContainer.getInstance('ComponentRegistry').getComponentByElement(targetElement, true);
        if (type === 'focus' && component && component.handleFocus) {
            this.lastFocusedComponent = component;
            component.handleFocus(event);
        }
        else if (type === 'blur' && component && component.handleBlur) {
            this.lastFocusedComponent = undefined;
            component.handleBlur(event);
        }
        else if (type === 'keydown') {
            if (this.fullscreenElement && event.key === 'escape') {
                this.closeFullscreen();
            }
            else if (this.lastFocusedComponent && this.lastFocusedComponent.handleKeyPress) {
                this.lastFocusedComponent.handleKeyPress(event);
            }
            else {
                this.handleKeyPress(event);
            }
        }
        else if (type === 'paste' && component && typeof component.handlePaste === 'function') {
            component.handlePaste(event);
        }
    }
    blurFocusedComponent() {
        if (document.activeElement && document.activeElement.blur) {
            document.activeElement.blur();
        }
    }
    /**
     * Called once when the browser loads initially, after drawing the Application component.
     * requestParams contains the query string parameters from the browser URL.
     * If requestParams (URL) contains a form action it will be opened in the viewport.
     * @param requestParams
     */
    start(requestParams) {
        const hash = window.location.hash;
        const formName = requestParams?._form || '';
        let homepage = isMobile() ? ENV('userMobileHomepage') : ENV('userHomepage');
        if (formName) {
            delete requestParams._form;
            DIContainer.getInstance('ScreenManager').open(formName, { ...{ requestParams }, ...{ hash } });
        }
        else if (homepage) {
            homepage = convertLegacyQ360Url(homepage);
            DIContainer.getInstance('ScreenManager').open(homepage, { ...{ hash } });
        }
        else {
            const screenRouter = DIContainer.getInstance('ScreenRouter');
            if (screenRouter.hasRoute('')) {
                DIContainer.getInstance('ScreenManager').open('', { ...{ requestParams }, ...{ hash } });
            }
        }
    }
    /**
     * This is for application level user preferences, not for filters on a random form or for grids etc.
     * ex: screenMode and theme.
     * @param preference
     * @param value
     * @param appName
     * @example
     * let app = DIContainer.getInstance<Application>('Application');
     * app.saveUserPreference('theme', 'dark');
     */
    saveUserPreference(preference, value, appName) {
        // TODO: AJAX call to save it to db, we already have dbo.Q360UserPreference and it already loads with the app
        Request.PUT({
            endpoint: 'application?_a=preferenceSave',
            loadingMessage: 'Saving preference',
            requestData: {
                'appname': appName ?? 'q360',
                'preference': preference,
                'value': value
            }
        }).subscribe(() => {
            console.log('saved preference', appName ?? 'q360', preference, value);
        });
        if (preference === 'datetimeFormat') {
            if (!value) {
                value = ENV('datetimeFormat', DateTimeManager.getLocaleBasedDatetimeFormat());
            }
            ENV(preference, value);
            ENV('dateFormat', DateTimeManager.getPartFromDateTimeFormat(ENV('datetimeFormat'), 'date'));
            ENV('timeFormat', DateTimeManager.getPartFromDateTimeFormat(ENV('datetimeFormat'), 'time'));
        }
        else {
            if (value === 'true') {
                ENV(preference, true);
            }
            else if (value === 'false') {
                ENV(preference, false);
            }
            else {
                ENV(preference, value);
            }
        }
        // we might choose to pass appName = '%' for some preferences that we would want to share accross multiple apps, probably just theme.
        appName = appName || ENV('appName');
        // also: once we are caching the application config loading, we will need this function to either modify that cache value or clear the cache. 
    }
    updateDatetimeFormat(format) {
        if (!format) {
            format = DateTimeManager.getLocaleBasedDatetimeFormat();
        }
        ENV('datetimeFormat', format);
        ENV('dateFormat', DateTimeManager.getPartFromDateTimeFormat(ENV('datetimeFormat'), 'date'));
        ENV('timeFormat', DateTimeManager.getPartFromDateTimeFormat(ENV('datetimeFormat'), 'time'));
        // TODO: trigger components to re-render possibly.
        DIContainer.getInstance('ComponentRegistry').forEach((component) => {
            component.handleDatetimeFormatSwitch(format);
        });
    }
    handleGridSizingSwitch(size) {
        document.body.setAttribute('data-grid-sizing', size);
    }
    handleContrastSwitch(contrast) {
        document.body.setAttribute('data-contrast', contrast);
    }
    updateSizing(size) {
        this.saveUserPreference('sizing', size, '%');
        document.body.setAttribute('data-sizing', size);
        DIContainer.getInstance('ComponentRegistry').forEach((component) => {
            component.handleSizingSwitch(size);
        });
    }
    updateTimezone(zone) {
        this.saveUserPreference('userTimezone', zone);
        DIContainer.getInstance('ComponentRegistry').forEach((component) => {
            component.handleTimezoneSwitch(zone);
        });
    }
    updateTheme(theme) {
        this.saveUserPreference('theme', theme, '%');
        document.body.setAttribute('data-theme', theme);
        DIContainer.getInstance('ComponentRegistry').forEach((component) => {
            component.handleThemeSwitch(theme);
        });
    }
    updateScreenMode(mode) {
        const variable = isMobile() ? 'screenModeMobile' : 'screenMode';
        if (mode && mode !== ENV(variable)) {
            this.saveUserPreference(variable, mode);
        }
        this.components.viewport.updateScreenMode(mode);
    }
    updateMobileMode(enabled) {
        if (enabled === isMobile()) {
            return;
        }
        ENV('mobile', enabled);
        if (enabled && ENV('screenMode') === 'window') {
            ENV('screenMode', 'tab');
            this.components.viewport.updateScreenMode('tab'); // dont want them to lose windows but cant let them have window mode on mobile, so going to tab mode instead of single mode.
        }
        const cacheBuster = Math.floor(Math.random() * 999999);
        const cssPath = `/dist/s360.m.css?${cacheBuster}`; // this is not correct for other apps besides Q360.
        if (!enabled) {
            const links = document.querySelectorAll('link[href^="/dist/s360.m.css"]');
            links.forEach(link => link.parentNode?.removeChild(link));
        }
        else {
            const link = document.createElement('link');
            link.rel = 'stylesheet';
            link.href = cssPath;
            document.head.appendChild(link);
        }
        DIContainer.getInstance('ComponentRegistry').forEach((component) => {
            component.handleMobileSwitch(enabled);
        });
    }
    /*updateViewMode(mode: 'tech'|'full') {
        if (mode !== ENV('viewMode')) {
            this.saveUserPreference('viewMode', mode);
            
            DIContainer.getInstance<ComponentRegistry>('ComponentRegistry').forEach((component) => {
                component.handleViewSwitch(mode);
            })
        }
    }*/
    /**
     * Logs out the current user.
     *
     * This method sends a POST request to the 'authenticate' endpoint with the 'logout' action.
     * After receiving a response from the server, it reloads the current window.
     *
     * @example
     * s360.Application.getInstance().logout();
     *
     * @remarks
     * The method uses the `Request.POST` method to initiate the logout process.
     * Once the logout is successful, the browser window is reloaded to ensure the user is sent to loginform.
     */
    logout() {
        const request = this.Request.POST({
            endpoint: 'authenticate?_a=logout',
            loadingMessage: 'Logging out...'
        });
        this.validateLogout().pipe(switchMap((valid) => valid ? request : of(false))).subscribe((loggedOut) => {
            if (loggedOut) {
                const redirectURL = '/' + ENV('appName');
                window.location.assign(redirectURL);
            }
        });
    }
    reset() {
    }
    validateLogout() {
        return of(true);
    }
    openFullscreen(element) {
        if (element.requestFullscreen) {
            element.requestFullscreen();
            element.classList.add('fullscreen');
        }
    }
    closeFullscreen() {
        if (document.fullscreenElement && document.exitFullscreen) {
            document.exitFullscreen();
        }
    }
    template() {
        let template = '';
        if (isMobile()) {
            this.rootElement.classList.add('s360MobileView');
        }
        template = `
<span data-componentid="navbar" ></span>
<div class="main-container">
	<span data-componentid="viewport" ></span>
</div>
`;
        return template;
    }
    /**
     * Static method to retrieve the singleton instance of `Application`.
     * Shorthand for DIContainer syntax.
     * @returns The singleton instance of `Application`.
     *
     * @example
     * // in devtools console
     * const app = s360.Application.getInstance();
     * app.open(...);
     */
    static getInstance() {
        // keeping getInstance on this class primarily just for convenience so it can be accessed in the console more easily.
        return DIContainer.getInstance('Application');
    }
}
Application.VERSION = '$Revision: 130060 $';
